/**
 * Category glyphs — one drawn 24×24 stroke glyph per idea (REDESIGN §7b).
 *
 * Same drawing conventions as `web/src/components/Icon.tsx` (the mockup's icon set): a 24×24
 * box, `fill: none`, `stroke: currentColor`, `stroke-width: 1.75`, round caps and joins,
 * rendered at 20px. Never emoji, never an external file.
 *
 * The values are SVG element markup rather than plain path data because several glyphs need a
 * `<circle>` or `<rect>`; they are module constants, exactly like `Icon.tsx`'s table, so no
 * user input can ever reach the `dangerouslySetInnerHTML` that renders them.
 */

export const CATEGORY_GLYPHS = {
  /** groceries / supermarket */
  cart:
    '<path d="M2.5 3.5h2.1l2.4 10.9a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 2-1.55L20 7.5H6.2"/><circle cx="9.5" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/>',
  /** restaurants, dining */
  utensils: '<path d="M4 2.5v6a2.5 2.5 0 0 0 5 0v-6"/><path d="M6.5 2.5v19"/><path d="M20 15V2.5a4.5 4.5 0 0 0-4 4.5v5a2 2 0 0 0 2 2h2zm0 0v6.5"/>',
  /** coffee & snacks */
  coffee: '<path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z"/><path d="M17 9.5h1.5a2.75 2.75 0 0 1 0 5.5H17"/><path d="M7.5 2.5v2.5M11 2.5v2.5M14.5 2.5v2.5"/>',
  /** bars, alcohol */
  glass: '<path d="M4.5 3.5h15l-7.5 8z"/><path d="M12 11.5v8"/><path d="M8 20.5h8"/>',
  /** shopping */
  bag: '<path d="M5.5 7h13l1 13.5H4.5z"/><path d="M8.5 9.5V6a3.5 3.5 0 0 1 7 0v3.5"/>',
  /** electronics */
  tv: '<rect x="2.5" y="5" width="19" height="12.5" rx="2"/><path d="M8 21h8"/><path d="M12 17.5V21"/>',
  /** clothes & accessories */
  shirt: '<path d="M8.5 3 4 5.5l1.8 4L8 8.6V21h8V8.6l2.2.9 1.8-4L15.5 3"/><path d="M8.5 3a3.5 3.5 0 0 0 7 0"/>',
  /** books & games */
  book: '<path d="M4 3.5h6a3 3 0 0 1 3 3v14a2.5 2.5 0 0 0-2.5-2.5H4z"/><path d="M20 3.5h-6a3 3 0 0 0-3 3v14a2.5 2.5 0 0 1 2.5-2.5H20z"/>',
  /** hobby & sports equipment */
  dumbbell:
    '<path d="M3.2 9.4v5.2M20.8 9.4v5.2"/><rect x="5.6" y="6.8" width="3.6" height="10.4" rx="1.3"/><rect x="14.8" y="6.8" width="3.6" height="10.4" rx="1.3"/><path d="M9.2 12h5.6"/>',
  /** transport in general, vehicles */
  car:
    '<path d="M5.5 11 7 6.8a2 2 0 0 1 1.9-1.3h6.2a2 2 0 0 1 1.9 1.3L18.5 11"/><path d="M4 11h16a1.5 1.5 0 0 1 1.5 1.5v4.5h-19v-4.5A1.5 1.5 0 0 1 4 11z"/><path d="M5 17v2.5M19 17v2.5"/><path d="M6.5 14h1.5M16 14h1.5"/>',
  /** fuel */
  fuel: '<path d="M4 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16"/><path d="M3 21h11"/><path d="M4.5 10.5h8"/><path d="M13 8h3.5a2 2 0 0 1 2 2v6.5a1.75 1.75 0 0 0 3.5 0V11l-2.5-3"/>',
  /** taxi */
  taxi:
    '<path d="M5.5 11 7 6.8a2 2 0 0 1 1.9-1.3h6.2a2 2 0 0 1 1.9 1.3L18.5 11"/><path d="M4 11h16a1.5 1.5 0 0 1 1.5 1.5v4.5h-19v-4.5A1.5 1.5 0 0 1 4 11z"/><path d="M5 17v2.5M19 17v2.5"/><path d="M9.5 5.5V3.5h5v2"/>',
  /** buses, public transport */
  bus: '<rect x="4" y="3" width="16" height="14" rx="2.5"/><path d="M4 10.5h16"/><path d="M7 21v-4M17 21v-4"/><path d="M7.5 13.8h.01M16.5 13.8h.01"/>',
  /** household, home */
  home: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h5v-6h4v6h5V9.5"/>',
  /** utilities, electricity */
  bolt: '<path d="M13.5 2.5 4 13.8h7L10.5 21.5 20 10.2h-7z"/>',
  /** communications, phone plans */
  phone: '<rect x="5.5" y="2" width="13" height="20" rx="2.5"/><path d="M12 18.2h.01"/>',
  /** healthcare */
  stethoscope:
    '<path d="M4.5 2.5v5a5 5 0 0 0 10 0v-5"/><path d="M3 2.5h3M13 2.5h3"/><path d="M9.5 12.5v2.5a5 5 0 0 0 9 1.5"/><circle cx="19.5" cy="15" r="2.5"/>',
  /** pharmacy */
  pill: '<path d="M14.5 2.9a5.5 5.5 0 0 1 7.8 7.8l-9.6 9.6a5.5 5.5 0 0 1-7.8-7.8z"/><path d="M8.5 8.5 16 16"/>',
  /** eyecare */
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
  /** beauty, personal care */
  sparkle: '<path d="M9 3.5 10.6 8 15 9.6 10.6 11.2 9 15.7 7.4 11.2 3 9.6 7.4 8z"/><path d="M17.5 14.5 18.5 17l2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z"/>',
  /** insurance */
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  /** education, kids */
  graduation: '<path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>',
  /** vacation */
  plane: '<path d="M10.5 20.5 12 16l7 4.2.9-2-5.4-5.2 5.4-4.5a2 2 0 0 0-2.4-3.2L12 9.6 5.2 4.4 3.6 6l4.6 6-3.7 2.5-2.5-.9-.9 1.6 3.4 2.2 2.6 2.9 1.6-.8z"/>',
  /** culture & events, leisure */
  ticket: '<path d="M3 8.5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2.5 2.5 0 0 0 0 5 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2.5 2.5 0 0 0 0-5z"/><path d="M14.5 6.5v9"/>',
  /** hobbies */
  palette:
    '<path d="M12 2.5a9.5 9.5 0 1 0 0 19 2.2 2.2 0 0 0 1.7-3.6 2.2 2.2 0 0 1 1.7-3.6h2.1A4 4 0 0 0 21.5 10 9.5 9.5 0 0 0 12 2.5z"/><path d="M7 10h.01M10.5 6.5h.01M15 7.5h.01M6.5 14.5h.01"/>',
  /** renovation & repairs */
  hammer: '<path d="M14.5 6 11 2.5 8.5 5l3.5 3.5z"/><path d="M12 8.5 9.5 11l-6 6a2.1 2.1 0 0 0 3 3l6-6 2.5-2.5"/><path d="M13.5 5 17 1.5l5.5 5.5L19 10.5z"/>',
  /** furniture & interior */
  sofa: '<path d="M4 11V7.5a2.5 2.5 0 0 1 2.5-2.5h11A2.5 2.5 0 0 1 20 7.5V11"/><path d="M4.5 11a2 2 0 0 0-2 2v4.5h19V13a2 2 0 0 0-2-2 2 2 0 0 0-2 2v1.5H6.5V13a2 2 0 0 0-2-2z"/><path d="M5 17.5V20M19 17.5V20"/>',
  /** garden */
  sprout: '<path d="M12 21v-8"/><path d="M12 13C12 8.6 8.6 5 4.5 5c0 4.4 3.4 8 7.5 8z"/><path d="M12 13c0-3.9 3-7 6.8-7 0 3.9-3 7-6.8 7z"/>',
  /** finance, generic money */
  coins: '<circle cx="8.5" cy="8.5" r="5.5"/><path d="M18.2 10.6A5.5 5.5 0 1 1 10.7 18"/><path d="M7.5 6.8h1.4v3.9"/>',
  /** fees, interest, percentages */
  percent: '<path d="M19 5 5 19"/><circle cx="7.5" cy="7.5" r="2.6"/><circle cx="16.5" cy="16.5" r="2.6"/>',
  /** transfers between accounts */
  transfer: '<path d="M16.5 3.5 20.5 7.5 16.5 11.5"/><path d="M3.5 7.5h17"/><path d="M7.5 12.5 3.5 16.5 7.5 20.5"/><path d="M20.5 16.5h-17"/>',
  /** Bit / PayBox — a payment sent from a phone */
  phoneSend: '<rect x="3.5" y="2" width="11" height="20" rx="2.5"/><path d="M9 18.2h.01"/><path d="M14.5 9h6"/><path d="M18 5.5 21.5 9 18 12.5"/>',
  /** cash deposits */
  banknote: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.6"/><path d="M5.5 9.5h.01M18.5 14.5h.01"/>',
  /** cheques */
  cheque: '<rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="M6 10h6M6 14h4"/><path d="M14.5 15.5 16.5 17.5 21 12.5"/>',
  /** loan repayments */
  loan:
    '<path d="M2.5 16.5 6 15l5.5 1.5h3a1.75 1.75 0 0 1 0 3.5H8"/><path d="M2.5 21.5 6 20l4 1.5"/><path d="M11.5 17h4a2 2 0 0 0 1.6-.8l3.4-4.4a1.9 1.9 0 0 0-3-2.3l-2.6 2.7"/><circle cx="14" cy="5.5" r="3"/>',
  /** mortgage */
  homeLoan: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><circle cx="12" cy="13.5" r="2"/><path d="M12 15.5V19M12 17.5h1.8"/>',
  /** refunds, returns, reversals */
  undo: '<path d="M3.5 8.5h7v-7"/><path d="M3.9 15.5a8.5 8.5 0 1 0 .8-7.5"/>',
  /** VAT, tax receipts */
  receipt: '<path d="M5 2.5v19l2-1 2 1 2-1 2 1 2-1 2 1v-19l-2 1-2-1-2 1-2-1-2 1z"/><path d="M8.5 7.5h7M8.5 11.5h7M8.5 15.5h4"/>',
  /** national insurance, government services */
  landmark: '<path d="M3 21.5h18"/><path d="M5.5 18V11M9.8 18V11M14.2 18V11M18.5 18V11"/><path d="M12 2.5 2.5 8h19z"/><path d="M3.5 21.5v-3.5h17v3.5"/>',
  /** business expenses */
  briefcase: '<rect x="2.5" y="6.5" width="19" height="14" rx="2"/><path d="M8.5 6.5V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5"/><path d="M2.5 12.5h19"/><path d="M10.5 12.5v2h3v-2"/>',
  /** savings, pension */
  piggy:
    '<path d="M18.5 5.5c-1.3 0-2.4 1.2-2.6 1.7-3.2-1.4-10.4-.3-10.4 4.8 0 1.7 0 2.9 1.9 4.3v3.2h3.7v-1.9h2.8v1.9h3.7v-3.8c.9-.5 1.6-1 1.9-1.9h1.9V9.5h-1.9c0-.9-.5-1.4-.9-1.9z"/><path d="M2.5 9.5v.9a1.9 1.9 0 0 0 1.9 1.9h.9"/><path d="M15.5 10.5h.01"/>',
  /** pets */
  paw: '<circle cx="7" cy="7.5" r="2.1"/><circle cx="12" cy="5.5" r="2.1"/><circle cx="17" cy="7.5" r="2.1"/><path d="M12 10.5c-2.6 0-5.5 2.4-5.5 5.3 0 2 1.5 3.2 3.2 3.2 1 0 1.6-.4 2.3-.4s1.3.4 2.3.4c1.7 0 3.2-1.2 3.2-3.2 0-2.9-2.9-5.3-5.5-5.3z"/>',
  /** foreign exchange, trading */
  exchange: '<circle cx="12" cy="12" r="9.2"/><path d="M2.8 12h18.4"/><path d="M12 2.8a13 13 0 0 1 0 18.4 13 13 0 0 1 0-18.4z"/>',
  /** money in and out — the `INCOMES_EXPENSES` family */
  arrows: '<path d="M7 20.5V4.5"/><path d="M3.2 8.3 7 4.5l3.8 3.8"/><path d="M17 3.5v16"/><path d="M13.2 15.7 17 19.5l3.8-3.8"/>',
  /** credit-card charge */
  card: '<rect x="2" y="5" width="20" height="14" rx="2.5"/><path d="M2 10h20"/><path d="M5.5 15h3"/>',
  /** returned standing orders, cheque returns, bank warnings */
  alert: '<path d="m21.6 18-8-14a2 2 0 0 0-3.4 0l-8 14a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3z"/><path d="M12 9.5v4M12 17h.01"/>',
  /** the neutral fallback — never a blank */
  dashed:
    '<path d="M10.1 2.18a9.93 9.93 0 0 1 3.8 0M17.6 3.71a9.95 9.95 0 0 1 2.69 2.7M21.82 10.1a9.93 9.93 0 0 1 0 3.8M20.29 17.6a9.95 9.95 0 0 1-2.7 2.69M13.9 21.82a9.94 9.94 0 0 1-3.8 0M6.4 20.29a9.95 9.95 0 0 1-2.69-2.7M2.18 13.9a9.93 9.93 0 0 1 0-3.8M3.71 6.4a9.95 9.95 0 0 1 2.7-2.69"/>',
} as const;

export type GlyphName = keyof typeof CATEGORY_GLYPHS;

/** The glyph shown when nothing else matches (REDESIGN §7b: "never a blank"). */
export const FALLBACK_GLYPH: GlyphName = 'dashed';

/** True when `name` is a glyph we actually draw. */
export function isGlyphName(name: string): name is GlyphName {
  return Object.prototype.hasOwnProperty.call(CATEGORY_GLYPHS, name);
}

/** The markup for a glyph, falling back to {@link FALLBACK_GLYPH}. */
export function glyphMarkup(name: string): string {
  return isGlyphName(name) ? CATEGORY_GLYPHS[name] : CATEGORY_GLYPHS[FALLBACK_GLYPH];
}
